import { loadAccessToken } from "../stores/authStore";
import { sanitizeChatDownloadFileName } from "./chatDownloadFileName";

const MIME_BY_EXT = {
  jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", gif: "image/gif",
  webp: "image/webp", bmp: "image/bmp", heic: "image/heic", heif: "image/heif",
  mp4: "video/mp4", mov: "video/quicktime", avi: "video/x-msvideo", mkv: "video/x-matroska",
  mp3: "audio/mpeg", wav: "audio/wav", m4a: "audio/mp4",
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  zip: "application/zip", txt: "text/plain",
  hwp: "application/x-hwp", hwpx: "application/vnd.hancom.hwpx",
};

function resolveMimeType(blobType, fileName) {
  if (blobType && blobType !== "application/octet-stream") return blobType;
  const ext = (fileName.split(".").pop() || "").toLowerCase();
  return MIME_BY_EXT[ext] || blobType || "application/octet-stream";
}

/**
 * 플랫폼별 파일 다운로드
 * - Android WebView : app.downloadFromBlob() 네이티브 브리지
 * - iOS Safari      : Web Share API (blob URL download 미지원)
 * - 데스크탑         : anchor click
 */
export async function downloadChatFile(url, fileName) {
  const isPresigned = url.includes("X-Amz-Signature") || url.includes("x-amz-signature");
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isAndroid = /Android/.test(navigator.userAgent);
  const safe =
    sanitizeChatDownloadFileName(fileName != null ? String(fileName) : "") || "download";

  const headers = {};
  if (!isPresigned) {
    const authToken = await loadAccessToken();
    if (authToken) headers.Authorization = `Bearer ${authToken}`;
  }

  const response = await fetch(url, { headers, cache: "reload" });
  if (!response.ok) throw new Error(`파일 다운로드 실패 (${response.status})`);

  const blob = await response.blob();

  if (isAndroid) {
    // www의 file_download()와 동일한 패턴: app.downloadFromBlob(base64DataUrl, fileName, mimeType)
    // app = Android JavascriptInterface 브리지 객체
    // blob.type이 비거나 application/octet-stream이면 파일명 확장자로 보정
    const mimeType = resolveMimeType(blob.type, safe);
    const reader = new FileReader();
    reader.onloadend = () => {
      // eslint-disable-next-line no-undef
      app.downloadFromBlob(reader.result, safe, mimeType);
    };
    reader.readAsDataURL(blob);
    return;
  }

  if (isIOS) {
    // iOS Safari: <a download>이 blob URL 미지원 → Web Share API 사용
    const file = new File([blob], safe, { type: blob.type });
    try {
      await navigator.share({ files: [file] });
    } catch (err) {
      if (err.name !== "AbortError") throw err;
    }
    return;
  }

  // 데스크탑: anchor click
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = safe;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}
